import {LobbyImage} from '@colonist/types'
import {TranslationArrayKeys, TranslationObject} from '@colonist/i18n'
import {BotsMatchType, MatchmakingMatchTypes, MatchType} from '@colonist/game-base-types'
import {createHeaderIn, createImgIn, createListItemIn, createParagraphIn, createUnorderedListIn, setElementInnerHTML, setElementInnerText, setElementOnClick} from '../../ui_utils/DomUtils'
import {createShuffleQueueIconStack} from '../../matchmaking/ShuffleQueueUtils'
import {getClientCKRankedState, getMatchTypePlayTime, getMatchTypePlayerAndBotCount, getShuffleQueueMapAndModeDescription, updateMatchmakingPlayButtonState, isMatchTypeCasualLocked, getCasualRedirectMatchType} from '../../matchmaking/MatchmakingUtilsClient'
import {clientI18n} from '../../ClientI18n'
import {ClientShuffleQueueController} from '../../shuffle/ClientShuffleQueueController'
import {CKRankedState} from '../../../../../shared/matchmaking/MatchmakingUtils'
import {ViewPopupFreePlayDiceRoll} from '../../ui_popup/ViewPopupFreePlayDiceRoll'

interface ModeDetailsData {
    icon: LobbyImage
    title: TranslationObject
    time: number | undefined
    playerCount: number
    botCount: number
    about: TranslationObject
    rules: TranslationArrayKeys
    faq: TranslationArrayKeys
}

export type StartGameCallback = (matchType: MatchType) => void
export type SelectModeCallback = (matchType: MatchType) => void

export abstract class UIModeSelectorDetailsController {
    protected readonly detailsContainer: HTMLDivElement
    private readonly backButton: HTMLDivElement
    private readonly titleElement: HTMLHeadingElement
    private readonly contentContainer: HTMLDivElement
    private readonly iconContainer: HTMLDivElement
    private readonly infoElement: HTMLDivElement
    private readonly timeElement: HTMLParagraphElement
    private readonly playerCountElement: HTMLParagraphElement
    private readonly botCountContainer: HTMLDivElement
    private readonly botCountElement: HTMLParagraphElement
    private readonly aboutElement: HTMLParagraphElement
    private readonly rulesListElement: HTMLDivElement
    private readonly faqListElement: HTMLDivElement
    private readonly playButton: HTMLButtonElement

    private currentMatchType: MatchType | undefined
    private readonly onStartGame: StartGameCallback
    private readonly onSelectMode: SelectModeCallback | undefined

    private static modeDetails: Map<MatchType, ModeDetailsData>

    protected constructor(onStartGame: StartGameCallback, onSelectMode?: SelectModeCallback) {
        this.onStartGame = onStartGame
        this.onSelectMode = onSelectMode

        this.detailsContainer = document.getElementById('mm-details-container') as HTMLDivElement
        this.backButton = document.getElementById('mm-details-back-button') as HTMLDivElement
        this.titleElement = document.getElementById('mm-details-title') as HTMLHeadingElement
        this.contentContainer = document.getElementById('mm-details-content') as HTMLDivElement
        this.iconContainer = document.getElementById('mm-details-icon-container') as HTMLDivElement
        this.infoElement = document.getElementById('mm-details-info') as HTMLDivElement
        this.timeElement = document.getElementById('mm-details-time') as HTMLParagraphElement
        this.playerCountElement = document.getElementById('mm-details-player-count') as HTMLParagraphElement
        this.botCountContainer = document.getElementById('mm-details-bot-count-container') as HTMLDivElement
        this.botCountElement = document.getElementById('mm-details-bot-count') as HTMLParagraphElement
        this.aboutElement = document.getElementById('mm-details-about') as HTMLParagraphElement
        this.rulesListElement = document.getElementById('mm-details-rules-list') as HTMLDivElement
        this.faqListElement = document.getElementById('mm-details-faq-list') as HTMLDivElement
        this.playButton = document.getElementById('mm-details-play-button') as HTMLButtonElement

        this.setupEventHandlers()
        void UIModeSelectorDetailsController.initModeDetails()

        ClientShuffleQueueController.registerChangeListener(async() => {
            await UIModeSelectorDetailsController.initModeDetails()
            if(this.currentMatchType != undefined) this.populateDetails(this.currentMatchType)
        })
    }

    private static async initModeDetails(): Promise<void> {
        const beginnerCounts = getMatchTypePlayerAndBotCount(BotsMatchType.BeginnerMode)
        const easyBotCounts = getMatchTypePlayerAndBotCount(BotsMatchType.Easy)
        const mediumBotCounts = getMatchTypePlayerAndBotCount(BotsMatchType.Medium)
        const hardBotCounts = getMatchTypePlayerAndBotCount(BotsMatchType.Hard)
        const casual1v1Counts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedClassic1v1)
        const casual4PlayerCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedClassic4P)
        const shuffleCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedShuffle)
        const ranked1v1Counts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedClassic1v1)
        const ranked4PlayerCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedClassic4P)
        const cnk4PlayerCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedCitiesAndKnights4P)
        const rush4PlayerCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedRush4P)

        UIModeSelectorDetailsController.modeDetails = new Map<MatchType, ModeDetailsData>([
            [BotsMatchType.BeginnerMode, {
                icon: LobbyImage.BotBeginner,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.beginner'},
                time: getMatchTypePlayTime(BotsMatchType.BeginnerMode),
                playerCount: beginnerCounts.playerCount,
                botCount: beginnerCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.beginner.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.beginner.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.beginner.faq',
            }],
            [BotsMatchType.Easy, {
                icon: LobbyImage.BotEasy,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.easy'},
                time: getMatchTypePlayTime(BotsMatchType.Easy),
                playerCount: easyBotCounts.playerCount,
                botCount: easyBotCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.easy.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.easy.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.easy.faq',
            }],
            [BotsMatchType.Medium, {
                icon: LobbyImage.BotMedium,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.medium'},
                time: getMatchTypePlayTime(BotsMatchType.Medium),
                playerCount: mediumBotCounts.playerCount,
                botCount: mediumBotCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.medium.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.medium.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.medium.faq',
            }],
            [BotsMatchType.Hard, {
                icon: LobbyImage.BotHard,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.hard'},
                time: getMatchTypePlayTime(BotsMatchType.Hard),
                playerCount: hardBotCounts.playerCount,
                botCount: hardBotCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.hard.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.hard.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.hard.faq',
            }],
            [MatchmakingMatchTypes.UnrankedClassic1v1, {
                icon: LobbyImage.GameMode1v1,
                title: {key: 'strings:homePage.matchmakingSelect.cards.1v1'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedClassic1v1),
                playerCount: casual1v1Counts.playerCount,
                botCount: casual1v1Counts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.casual1v1.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.casual1v1.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.casual1v1.faq',
            }],
            [MatchmakingMatchTypes.UnrankedClassic4P, {
                icon: LobbyImage.GameModeBase,
                title: {key: 'strings:homePage.matchmakingSelect.cards.4Player'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedClassic4P),
                playerCount: casual4PlayerCounts.playerCount,
                botCount: casual4PlayerCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.casual4Player.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.casual4Player.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.casual4Player.faq',
            }],
            [MatchmakingMatchTypes.UnrankedShuffle, {
                icon: LobbyImage.GameModeShuffle,
                title: {key: 'strings:homePage.unranked.shuffle'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedShuffle),
                playerCount: shuffleCounts.playerCount,
                botCount: shuffleCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.shuffle.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.shuffle.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.shuffle.faq',
            }],
            [MatchmakingMatchTypes.RankedClassic1v1, {
                icon: LobbyImage.GameModeRanked1v1,
                title: {key: 'strings:homePage.matchmakingSelect.cards.1v1'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedClassic1v1),
                playerCount: ranked1v1Counts.playerCount,
                botCount: ranked1v1Counts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.ranked1v1.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.ranked1v1.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.ranked1v1.faq',
            }],
            [MatchmakingMatchTypes.RankedClassic4P, {
                icon: LobbyImage.GameModeRanked4P,
                title: {key: 'strings:homePage.matchmakingSelect.cards.4Player'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedClassic4P),
                playerCount: ranked4PlayerCounts.playerCount,
                botCount: ranked4PlayerCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.ranked4P.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.ranked4P.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.ranked4P.faq',
            }],
            [MatchmakingMatchTypes.RankedCitiesAndKnights4P, {
                icon: LobbyImage.GameModeCnK,
                title: {key: 'strings:homePage.matchmakingSelect.cards.citiesAndKnights'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedCitiesAndKnights4P),
                playerCount: cnk4PlayerCounts.playerCount,
                botCount: cnk4PlayerCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.citiesAndKnights.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.citiesAndKnights.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.citiesAndKnights.faq',
            }],
            [MatchmakingMatchTypes.RankedRush4P, {
                icon: LobbyImage.GameModeColonistRush,
                title: {key: 'strings:homePage.matchmakingSelect.cards.rush'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedRush4P),
                playerCount: rush4PlayerCounts.playerCount,
                botCount: rush4PlayerCounts.botCount,
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.rankedRush.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.rankedRush.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.rankedRush.faq',
            }],
        ])
    }

    show(matchType: MatchType) {
        this.currentMatchType = matchType
        this.populateDetails(matchType)
        this.detailsContainer.classList.remove('hidden')
        this.contentContainer.scrollTop = 0
        this.onShow()
    }

    // eslint-disable-next-line class-methods-use-this
    protected onShow() {
        return
    }

    hide() {
        this.detailsContainer.classList.add('hidden')
        this.onHide()
    }

    // eslint-disable-next-line class-methods-use-this
    protected onHide() {
        return
    }

    private setupEventHandlers() {
        setElementOnClick(this.backButton, () => {
            this.hide()
        })

        setElementOnClick(this.playButton, () => {
            this.handlePlayButtonClick()
        })
    }

    private handlePlayButtonClick() {
        const matchType = this.currentMatchType
        if(matchType == undefined) return

        if(isMatchTypeCasualLocked(matchType)) {
            const casualMatchType = getCasualRedirectMatchType(matchType)
            if(casualMatchType != undefined && this.onSelectMode != undefined) {
                this.hide()
                this.onSelectMode(casualMatchType)
            }
            return
        }

        if(matchType == MatchmakingMatchTypes.RankedCitiesAndKnights4P) {
            const ckRankedState = getClientCKRankedState()
            if(ckRankedState == CKRankedState.CanRollDice) {
                new ViewPopupFreePlayDiceRoll()
                return
            }
        }

        this.onStartGame(matchType)
    }

    private populateDetails(matchType: MatchType) {
        const modeDetails = UIModeSelectorDetailsController.modeDetails
        const detailsData = modeDetails.get(matchType)
        if(detailsData == undefined) return

        setElementInnerText(this.titleElement, detailsData.title)

        setElementInnerHTML(this.iconContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        const shuffleQueueData = ClientShuffleQueueController.shuffleQueueData
        if(matchType == MatchmakingMatchTypes.UnrankedShuffle && shuffleQueueData != undefined) {
            const stackedIcon = createShuffleQueueIconStack(shuffleQueueData.mapId, shuffleQueueData.modeId)
            if(stackedIcon != undefined) this.iconContainer.appendChild(stackedIcon)
        } else {
            createImgIn(this.iconContainer, detailsData.icon, 'mm-details-icon')
        }

        if(detailsData.time != undefined) setElementInnerText(this.timeElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: detailsData.time.toString()}})

        setElementInnerText(this.playerCountElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: detailsData.playerCount.toString()}})

        this.botCountContainer.classList.toggle('hidden', detailsData.botCount == 0)
        setElementInnerText(this.botCountElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: detailsData.botCount.toString()}})

        const shouldHideInfo = matchType == MatchmakingMatchTypes.UnrankedShuffle && shuffleQueueData == undefined
        this.infoElement.classList.toggle('hidden', shouldHideInfo)

        setElementInnerText(this.aboutElement, detailsData.about)

        const existingShuffleMapMode = document.getElementById('mm-details-shuffle-map-mode')
        existingShuffleMapMode?.remove()

        if(matchType == MatchmakingMatchTypes.UnrankedShuffle && shuffleQueueData != undefined) {
            const shuffleMapAndMode = getShuffleQueueMapAndModeDescription(shuffleQueueData)
            const shuffleMapAndModeText = clientI18n.t(shuffleMapAndMode.key, shuffleMapAndMode.options) + '!'
            createParagraphIn(this.aboutElement.parentElement as HTMLElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: shuffleMapAndModeText}}, '', 'mm-details-shuffle-map-mode')
        }

        setElementInnerHTML(this.rulesListElement, {key: 'strings:colonistCommon.utils.emptyString'})
        const rulesArray = clientI18n.tArray(detailsData.rules)
        if(rulesArray.length == 1) {
            createParagraphIn(this.rulesListElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rulesArray[0]}}, '', '')
        } else {
            const rulesList = createUnorderedListIn(this.rulesListElement, '', '')
            for(const rule of rulesArray) createListItemIn(rulesList, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rule}}, '', '')
        }

        setElementInnerHTML(this.faqListElement, {key: 'strings:colonistCommon.utils.emptyString'})
        const faqArray = clientI18n.tArray(detailsData.faq) as {question: string, answer: string}[]
        for(const faqItem of faqArray) {
            createHeaderIn(this.faqListElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: faqItem.question}}, '')
            createParagraphIn(this.faqListElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: faqItem.answer}}, '')
        }

        updateMatchmakingPlayButtonState(this.playButton, matchType)
    }
}

